import { assembleReportingPeriodTypes } from '@/containers/DigitalAnalyst/components/ReportingPeriodFilter';
import { assembleDocumentTypes, filterDocsByReportingFilter, filterDocsByTags } from '@/utils/doc';
import { filterDuplicateDocuments } from '@/utils/usefulFuncs';
import React from 'react';

export const FilterableDocumentsContext = React.createContext({
  documents: [],
  filteredDocuments: [],

  documentTypes: [],
  reportingPeriodTypes: [],

  setDocuments: () => {},
  addDocuments: () => {},

  currentFilter: {},
  applyFilter: (filterProperties) => {},
  clearFilter: () => {},

  setLoadingDocuments: () => {},
});

export const FilterableDocumentsContextProvider = ({ children, docGetter }) => {
  const [documents, _setDocuments] = React.useState([]);
  const [documentTypes, setDocumentTypes] = React.useState([]);
  const [reportingPeriodTypes, setReportingPeriodTypes] = React.useState([]);
  const [filteredDocuments, setFilteredDocuments] = React.useState([]);

  const [loadingDocuments, setLoadingDocuments] = React.useState(false);

  const [filter, setFilter] = React.useState({});

  React.useEffect(() => {
    const fetchDocuments = async () => {
      const docs = await docGetter();
      if (docs) {
        setDocuments(docs);
      }
    };
    fetchDocuments();
  }, []);

  const setDocuments = (documents) => {
    // Dedupe by id — the API can return the same document twice, which otherwise
    // produces duplicate React keys in SelectableDocumentsList and breaks the list.
    const deduped = filterDuplicateDocuments(documents || []);
    _setDocuments(deduped);
    setDocumentTypes(assembleDocumentTypes(deduped));
    const nextReportingPeriodTypes = assembleReportingPeriodTypes(deduped);
    setReportingPeriodTypes(nextReportingPeriodTypes);
    setFilteredDocuments(deduped);
  };

  const addDocuments = (docsToAdd, filterProperties) => {
    const nextDocuments = filterDuplicateDocuments([...documents, ...docsToAdd]);
    _setDocuments(nextDocuments);
    setDocumentTypes(assembleDocumentTypes(nextDocuments));
    const nextReportingPeriodTypes = assembleReportingPeriodTypes(nextDocuments);
    setReportingPeriodTypes(nextReportingPeriodTypes);

    if (filterProperties) {
      applyFilter(filterProperties, nextDocuments);
    }
  };

  const updateCurrentFilter = (filterProperties) => {
    const nextFilter = { ...filter, ...filterProperties };
    setFilter(nextFilter);
    return nextFilter;
  };

  const filterFunction = (filter, forcedDocuments = documents) => {
    let filtered = forcedDocuments;
    if (filter.tags) {
      filtered = filterDocsByTags(filtered, filter.tags);
    }
    if (filter.doc_type) {
      if (filter.doc_type === 'CSV') {
        filtered = filtered.filter((doc) => doc.content_type === 'text/csv');
      } else {
        filtered = filtered.filter((doc) => doc.doc_type === filter.doc_type);
      }
    }
    if (filter.activeReportingPeriod) {
      filtered = filterDocsByReportingFilter(filtered, filter.activeReportingPeriod);
    }

    return filtered;
  };

  const applyFilter = (filterProperties, documents) => {
    const nextFilter = updateCurrentFilter(filterProperties);
    const filtered = filterFunction(nextFilter, documents);
    setFilteredDocuments(filtered);
  };

  const clearFilter = (key) => {
    if (!key) {
      setFilteredDocuments(documents);
      setFilter({});
    } else {
      const nextFilter = { ...filter };
      delete nextFilter[key];
      setFilter(nextFilter);
      const filtered = filterFunction(nextFilter);
      setFilteredDocuments(filtered);
    }
  };

  const contextValue = React.useMemo(
    () => ({
      documents,
      filteredDocuments,

      filter,
      documentTypes,
      reportingPeriodTypes,

      setDocuments,
      addDocuments,

      applyFilter,
      clearFilter,

      loadingDocuments,
      setLoadingDocuments,
    }),
    [
      documents,
      filteredDocuments,

      documentTypes,
      filter,
      reportingPeriodTypes,

      setDocuments,
      addDocuments,

      applyFilter,
      clearFilter,

      loadingDocuments,
      setLoadingDocuments,
    ]
  );

  return <FilterableDocumentsContext.Provider value={contextValue}>{children}</FilterableDocumentsContext.Provider>;
};
